import { useState, useEffect, useCallback, useRef } from "react";
import { ConfirmModal, ActionButtons, useToast } from "../../../../shared/components/SharedUI";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAsientos } from "../hooks/useAsientos";
import apiFetch from "../../../../shared/services/apiClient";
import { parseMoney, sanitizeMoneyTyping } from "../../../../shared/utils/parseMoney";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { downloadExcel, uploadExcel } from "../../shared/excelService";
import { TableToolbar, FilterField, FilterInput, ToolbarSelect, FilterPills } from "../../shared/TableToolbar";
import styles from "../../invoices/styles/invoice.module.css";

const TIPO_COLOR = {
  MANUAL:  { bg: "rgba(167,139,250,0.1)", border: "rgba(167,139,250,0.3)", text: "#a78bfa" },
  FACTURA: { bg: "rgba(74,222,128,0.1)",  border: "rgba(74,222,128,0.3)",  text: "#4ade80" },
  CXP:     { bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.3)",  text: "#fbbf24" },
  RECIBO:  { bg: "rgba(96,165,250,0.1)",  border: "rgba(96,165,250,0.3)",  text: "#60a5fa" },
};

// Formato con separador de miles (coma) y 2 decimales: 191306.39 → "191,306.39"
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Input de monto con separadores de miles ───────────────────────────────────
// Muestra el monto formateado (191,306.39) cuando NO está enfocado, y el número
// crudo y editable cuando el usuario hace clic para escribir.
function MoneyInput({ value, onChange, style, disabled = false, placeholder = "0.00" }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState("");
  const display = focused ? raw : (Number(value) ? fmt(value) : "");
  return (
    <input
      type="text"
      inputMode="decimal"
      disabled={disabled}
      value={display}
      placeholder={placeholder}
      style={style}
      onFocus={() => { setFocused(true); setRaw(Number(value) ? String(value) : ""); }}
      onChange={(e) => {
        const v = sanitizeMoneyTyping(e.target.value);
        setRaw(v);
        onChange(parseMoney(v));
      }}
      onBlur={() => setFocused(false)}
    />
  );
}

// ── Selector de cuenta con búsqueda (datalist) ────────────────────────────────
// Permite escribir el CÓDIGO o el NOMBRE; el navegador filtra las opciones.
// Usa un <datalist> compartido (listId) para no duplicar la lista por fila.
function CuentaDatalistInput({ value, codigo, nombre, cuentas, onSelect, style, listId }) {
  const sel = cuentas.find((c) => String(c.id) === String(value));
  const current = sel ? `${sel.codigo} — ${sel.nombre}` : (codigo ? `${codigo} — ${nombre}` : "");
  const [text, setText] = useState(current);
  useEffect(() => { setText(current); }, [current]);
  return (
    <input
      list={listId}
      value={text}
      style={style}
      placeholder="Código o nombre…"
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        const exacta = cuentas.find(
          (c) => `${c.codigo} — ${c.nombre}` === v || c.codigo === v || c.nombre === v
        );
        if (exacta) onSelect(exacta.id);
      }}
    />
  );
}

// ── Celda de monto con toggle CRC/USD ─────────────────────────────────────────
// En vista USD, si el asiento tiene monto en ₡ pero $0 en dólares, lo marca en
// ámbar ("sin $") para que el contador detecte líneas sin su valor en dólares.
function MontoCelda({ crc, usd, isUSD, color }) {
  const crcN = Number(crc) || 0;
  const usdN = Number(usd) || 0;
  if (isUSD) {
    const falta = crcN > 0.005 && usdN < 0.005;
    return (
      <div style={{ textAlign: "right", fontFamily: "monospace", fontSize: 13,
        color: falta ? "#fbbf24" : color }}
        title={falta ? "Este asiento no tiene valor en dólares" : undefined}>
        {falta
          ? <><i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 10, marginRight: 4 }} />sin $</>
          : `$${fmt(usdN)}`}
      </div>
    );
  }
  return (
    <div style={{ textAlign: "right", fontFamily: "monospace", fontSize: 13, color }}>
      ₡{fmt(crcN)}
    </div>
  );
}

// ── Modal de detalle / edición — theme-aware ──────────────────────────────────
function AsientoDetalleModal({ asientoId, onClose, onDeleted, onSaved }) {
  const { t }       = useTranslation();
  const multimoneda = useMultimoneda();
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [concepto, setConcepto] = useState("");
  const [fecha,    setFecha]    = useState("");
  const [lineas,   setLineas]   = useState([]);
  const [cuentas,  setCuentas]  = useState([]);
  const [tipoCambio, setTipoCambio] = useState(1);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm,  setConfirm]  = useState(false);
  const [toastMsg, setToastMsg] = useState({ msg: "", ok: true });

  const showT = (msg, ok = true) => { setToastMsg({ msg, ok }); setTimeout(() => setToastMsg({ msg: "", ok: true }), 3500); };

  function loadAsiento() {
    setLoading(true);
    apiFetch(`/api/finance/asientos/${asientoId}/`)
      .then(r => r.json())
      .then(d => {
        setData(d); setConcepto(d.concepto); setFecha(d.fecha || "");
        const ls = d.lineas?.map(l => ({
          cuenta: l.cuenta, cuenta_codigo: l.cuenta_codigo, cuenta_nombre: l.cuenta_nombre,
          debito: Number(l.debito) || 0, credito: Number(l.credito) || 0, descripcion: l.descripcion || "",
          debito_usd: Number(l.debito_usd) || 0, credito_usd: Number(l.credito_usd) || 0,
          moneda: l.moneda || "CRC", tipo_cambio: Number(l.tipo_cambio) || 1,
        })) || [];
        setLineas(ls);
        // Derivar el tipo de cambio del asiento: del tipo_cambio guardado, o
        // calculándolo de una línea que tenga monto en ₡ y en $ a la vez.
        let tc = 0;
        for (const l of ls) {
          if (Number(l.tipo_cambio) > 1) { tc = Number(l.tipo_cambio); break; }
          const crc = l.debito || l.credito;
          const usd = l.debito_usd || l.credito_usd;
          if (crc > 0 && usd > 0) { tc = crc / usd; break; }
        }
        setTipoCambio(tc > 1 ? Math.round(tc * 100) / 100 : 1);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadAsiento();
    apiFetch("/api/finance/catalogo/?is_detalle=true&page_size=1000&ordering=codigo")
      .then(r => r.json()).then(d => setCuentas(d.results || d)).catch(() => {});
  }, [asientoId]);

  const totalD    = lineas.reduce((s, l) => s + (l.debito       || 0), 0);
  const totalH    = lineas.reduce((s, l) => s + (l.credito      || 0), 0);
  const totalDusd = lineas.reduce((s, l) => s + (Number(l.debito_usd)  || 0), 0);
  const totalHusd = lineas.reduce((s, l) => s + (Number(l.credito_usd) || 0), 0);

  const balanced    = Math.abs(totalD - totalH) < 0.001;
  const hasUsd      = multimoneda && (totalDusd > 0 || totalHusd > 0);
  const balancedUsd = !hasUsd || Math.abs(totalDusd - totalHusd) < 0.001;
  const fullyOk     = balanced && balancedUsd;

  const updL = (idx, field, val) => setLineas(ls => ls.map((l, i) => i === idx ? { ...l, [field]: val } : l));
  const addL = () => setLineas(ls => [...ls, {
    cuenta: null, cuenta_codigo: "", cuenta_nombre: "",
    debito: 0, credito: 0, descripcion: "",
    debito_usd: 0, credito_usd: 0,
  }]);
  const delL = (idx) => { if (lineas.length <= 2) { showT("Mínimo 2 líneas", false); return; } setLineas(ls => ls.filter((_, i) => i !== idx)); };
  const chgC = (idx, id) => {
    const c = cuentas.find(x => String(x.id) === String(id));
    setLineas(ls => ls.map((l, i) => i === idx ? { ...l, cuenta: c?.id || null, cuenta_codigo: c?.codigo || "", cuenta_nombre: c?.nombre || "" } : l));
  };

  // Tipo de cambio del asiento (el del momento en que se creó — no el actual)
  const TCnum = Number(tipoCambio) || 1;
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // Actualiza un monto SIN convertir automáticamente. Solo limpia el lado opuesto
  // (una línea es débito O crédito), pero respeta la otra moneda del mismo lado.
  // Así una línea de un solo lado (ej. diferencial cambiario, que solo tiene $ o
  // solo ₡) NO se convierte sola. La conversión es a pedido con el botón ↔.
  function setMonto(idx, campo, val) {
    val = Number(val) || 0;
    setLineas(ls => ls.map((l, i) => {
      if (i !== idx) return l;
      if (campo === "debito")      return { ...l, debito: val, credito: 0, credito_usd: 0 };
      if (campo === "debito_usd")  return { ...l, debito_usd: val, credito: 0, credito_usd: 0 };
      if (campo === "credito")     return { ...l, credito: val, debito: 0, debito_usd: 0 };
      if (campo === "credito_usd") return { ...l, credito_usd: val, debito: 0, debito_usd: 0 };
      return l;
    }));
  }

  // Conversión A PEDIDO de una línea: (re)calcula el monto en la otra moneda del
  // lado que ya tiene valor, usando el TC del asiento. Sobrescribe el valor previo
  // para que volver a tocar el botón con un TC distinto recalcule. Respeta líneas
  // de un solo lado (solo ₡ o solo $): rellena su moneda faltante a partir de la
  // que tienen. Solo se ejecuta cuando el usuario lo pide (no es automático).
  function convertLine(idx) {
    if (TCnum <= 1) { showT("Definí primero el TC del asiento.", false); return; }
    setLineas(ls => ls.map((l, i) => {
      if (i !== idx) return l;
      const nl = { ...l };
      // Débito: si hay ₡ → calcular $; si solo hay $ → calcular ₡
      if (Number(l.debito) > 0)          nl.debito_usd  = r2(Number(l.debito) / TCnum);
      else if (Number(l.debito_usd) > 0) nl.debito      = r2(Number(l.debito_usd) * TCnum);
      // Crédito: idem
      if (Number(l.credito) > 0)          nl.credito_usd = r2(Number(l.credito) / TCnum);
      else if (Number(l.credito_usd) > 0) nl.credito     = r2(Number(l.credito_usd) * TCnum);
      return nl;
    }));
  }

  async function handleSave() {
    if (!balanced)    { showT(t("asientos.noBalanceado"), false); return; }
    if (!balancedUsd) { showT("Asiento USD no balanceado. Revisá los montos en dólares.", false); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`/api/finance/asientos/${asientoId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          concepto,
          fecha,
          lineas: lineas.map(l => ({
            cuenta:      l.cuenta,
            debito:      l.debito      || 0,
            credito:     l.credito     || 0,
            descripcion: l.descripcion || "",
            debito_usd:  multimoneda ? (Number(l.debito_usd)  || 0) : 0,
            credito_usd: multimoneda ? (Number(l.credito_usd) || 0) : 0,
            moneda:      multimoneda && (Number(l.debito_usd) > 0 || Number(l.credito_usd) > 0) ? "USD" : "CRC",
            tipo_cambio: multimoneda && TCnum > 1 ? TCnum : 1,
          })),
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || JSON.stringify(e)); }
      showT("Guardado correctamente ✓");
      setEditMode(false); loadAsiento(); onSaved?.();
    } catch (e) { showT(e.message || "Error al guardar", false); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try { await apiFetch(`/api/finance/asientos/${asientoId}/`, { method: "DELETE" }); onDeleted?.(); onClose(); }
    catch (_) {} finally { setDeleting(false); }
  }

  const inpSt = {
    background: "var(--bg-input)", border: "1px solid var(--border-input)",
    borderRadius: 5, color: "var(--text-primary)", fontSize: 13,
    padding: "5px 8px", outline: "none", width: "100%", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.75)",
      backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}>
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 8, width: "min(840px,96vw)", maxHeight: "92vh", display: "flex",
        flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>

        {loading ? (
          <div style={{ textAlign: "center", padding: 48, color: "var(--text-secondary)" }}>
            <i className="fa-solid fa-circle-notch fa-spin"
               style={{ color: "#f97316", fontSize: "1.4rem", display: "block", marginBottom: 8 }}/>
            {t("common.cargando")}
          </div>
        ) : data ? (<>

          {/* Header */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-card)",
            display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text-primary)", fontFamily: "monospace" }}>
                {data.numero}
              </h2>
              <p style={{ margin: "3px 0 0", color: "var(--text-secondary)", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                {editMode ? (
                  <input
                    type="date"
                    value={fecha}
                    onChange={e => setFecha(e.target.value)}
                    style={{
                      background: "var(--bg-input)", border: "1px solid #f97316",
                      borderRadius: 5, color: "var(--text-primary)", fontSize: 13,
                      padding: "3px 7px", outline: "none",
                    }}
                  />
                ) : (
                  <span>{data.fecha}</span>
                )}
                · {data.tipo_display}
                {editMode && multimoneda && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
                    marginLeft: 4, paddingLeft: 10, borderLeft: "1px dashed var(--border-card)" }}>
                    <i className="fa-solid fa-right-left" style={{ fontSize: 10, color: "#f97316" }}/>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>TC del asiento</span>
                    <input
                      type="number" min="0" step="0.01" value={tipoCambio}
                      onChange={e => setTipoCambio(Number(e.target.value) || 1)}
                      title="Tipo de cambio con el que se creó el asiento. Se usa solo cuando tocás el botón ↔ de una línea para convertir."
                      style={{ width: 78, background: "var(--bg-input)", border: "1px solid #f97316",
                        borderRadius: 5, color: "var(--text-primary)", fontSize: 12,
                        padding: "3px 6px", outline: "none", textAlign: "right" }}/>
                    {TCnum > 1 && (
                      <button type="button"
                        onClick={() => lineas.forEach((_, i) => convertLine(i))}
                        title="Completar el monto faltante (₡ o $) en TODAS las líneas, al TC"
                        style={{ background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.4)",
                          borderRadius: 5, color: "#f97316", cursor: "pointer", fontSize: 11,
                          padding: "3px 8px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 10 }}/> Convertir todas
                      </button>
                    )}
                  </span>
                )}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {!editMode && (
                <button type="button" onClick={() => setEditMode(true)}
                  style={{ padding: "6px 12px", background: "transparent", border: "1px solid var(--border-card)",
                    borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer", fontSize: 13,
                    display: "flex", alignItems: "center", gap: 6 }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = "#f97316"; e.currentTarget.style.color = "#f97316"; }}
                  onMouseOut={e  => { e.currentTarget.style.borderColor = "var(--border-card)"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
                  <i className="fa-solid fa-pen-to-square"/> {t("common.editar")}
                </button>
              )}
              {!editMode && (!confirm ? (
                <button type="button" onClick={() => setConfirm(true)}
                  style={{ padding: "6px 12px", background: "transparent", border: "1px solid var(--border-card)",
                    borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer", fontSize: 13,
                    display: "flex", alignItems: "center", gap: 6 }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = "#ef4444"; e.currentTarget.style.color = "#f87171"; }}
                  onMouseOut={e  => { e.currentTarget.style.borderColor = "var(--border-card)"; e.currentTarget.style.color = "var(--text-secondary)"; }}>
                  <i className="fa-solid fa-trash"/> {t("common.eliminar")}
                </button>
              ) : (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ color: "#f87171", fontSize: 12 }}>¿{t("common.eliminar")}?</span>
                  <button type="button" onClick={handleDelete} disabled={deleting}
                    style={{ padding: "5px 10px", background: "rgba(239,68,68,0.15)", border: "1px solid #ef4444",
                      borderRadius: 6, color: "#f87171", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                    {deleting ? <i className="fa-solid fa-circle-notch fa-spin"/> : t("common.siEliminar")}
                  </button>
                  <button type="button" onClick={() => setConfirm(false)}
                    style={{ padding: "5px 10px", background: "transparent", border: "1px solid var(--border-card)",
                      borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer", fontSize: 12 }}>
                    {t("common.cancelar")}
                  </button>
                </div>
              ))}
              <button type="button" onClick={onClose}
                style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "1.1rem", padding: "4px 6px" }}>
                <i className="fa-solid fa-xmark"/>
              </button>
            </div>
          </div>

          {/* Toast */}
          {toastMsg.msg && (
            <div style={{ padding: "8px 20px",
              background: toastMsg.ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)",
              borderBottom: "1px solid var(--border-card)",
              color: toastMsg.ok ? "#4ade80" : "#f87171", fontSize: 13,
              display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <i className={`fa-solid ${toastMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}/>
              {toastMsg.msg}
            </div>
          )}

          {/* Aviso tipo automático */}
          {editMode && data.tipo !== "MANUAL" && (
            <div style={{ padding: "10px 20px", background: "rgba(251,191,36,0.08)",
              borderBottom: "1px solid rgba(251,191,36,0.2)", fontSize: 12, color: "#fbbf24",
              display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <i className="fa-solid fa-triangle-exclamation"/>
              {t("asientos.asientoAutomatico")}
            </div>
          )}
          {/* Aviso: multimoneda desactivado — columnas USD en solo lectura */}
          {editMode && !multimoneda && (
            <div style={{ padding: "8px 20px", background: "rgba(100,116,139,0.07)",
              borderBottom: "1px solid rgba(100,116,139,0.15)", fontSize: 12, color: "var(--text-secondary)",
              display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <i className="fa-solid fa-lock" style={{ fontSize: 10 }}/>
              Los campos en dólares (USD) están desactivados. Activá Multimoneda en Configuración para editarlos.
            </div>
          )}

          {/* Concepto */}
          <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--border-card)", flexShrink: 0 }}>
            {editMode ? (
              <div>
                <label style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase",
                  letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>
                  {t("asientos.concepto")}
                </label>
                <input type="text" value={concepto} onChange={e => setConcepto(e.target.value)}
                  style={{ ...inpSt, border: "1px solid #f97316" }}/>
              </div>
            ) : (
              <p style={{ margin: 0, color: "var(--text-primary)", fontSize: 14 }}>{data.concepto}</p>
            )}
          </div>

          {/* Datalist compartido de cuentas (para búsqueda por código o nombre) */}
          {editMode && (
            <datalist id="cuentas-asiento-list">
              {cuentas.map(c => (
                <option key={c.id} value={`${c.codigo} — ${c.nombre}`} />
              ))}
            </datalist>
          )}

          {/* Tabla líneas */}
          <div style={{ flex: 1, overflowY: "auto", scrollbarWidth: "thin" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, background: "var(--bg-card)", zIndex: 1 }}>
                <tr style={{ borderBottom: "1px solid var(--border-card)" }}>
                  {[t("catalogo.nombre"), t("common.descripcion")].map(h => (
                    <th key={h} style={{ padding: "10px 12px", color: "var(--text-secondary)", fontSize: 11,
                      fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "left" }}>
                      {h}
                    </th>
                  ))}
                  <th style={{ padding: "10px 12px", color: "#4ade80", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                    {t("asientos.debe")} ₡
                  </th>
                  <th style={{ padding: "10px 12px", color: "#60a5fa", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                    {t("asientos.haber")} ₡
                  </th>
                  <th style={{ padding: "10px 12px", color: "#86efac", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right", borderLeft: "1px dashed var(--border-card)" }}>
                    {t("asientos.debe")} $
                  </th>
                  <th style={{ padding: "10px 12px", color: "#93c5fd", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                    {t("asientos.haber")} $
                  </th>
                  {editMode && <th style={{ width: 32 }}/>}
                </tr>
              </thead>
              <tbody>
                {lineas.map((l, i) => (
                  <tr key={i} style={{ background: "transparent",
                    borderBottom: "1px solid var(--border-card)" }}>
                    <td style={{ padding: "6px 8px" }}>
                      {editMode ? (
                        <CuentaDatalistInput
                          value={l.cuenta}
                          codigo={l.cuenta_codigo}
                          nombre={l.cuenta_nombre}
                          cuentas={cuentas}
                          onSelect={(id) => chgC(i, id)}
                          listId="cuentas-asiento-list"
                          style={inpSt}
                        />
                      ) : (
                        <>
                          <span style={{ fontFamily: "monospace", color: "#f97316", fontSize: 11 }}>{l.cuenta_codigo}</span>
                          <span style={{ color: "var(--text-secondary)", marginLeft: 8 }}>{l.cuenta_nombre}</span>
                        </>
                      )}
                    </td>
                    <td style={{ padding: "6px 8px", maxWidth: 220, overflow: "hidden" }}>
                      {editMode ? (
                        <input type="text" value={l.descripcion} style={inpSt}
                          onChange={e => updL(i, "descripcion", e.target.value)}
                          placeholder={t("common.descripcion")}/>
                      ) : (
                        <span style={{ padding: "9px 12px", color: "var(--text-secondary)",
                          display: "block", whiteSpace: "nowrap", overflow: "hidden",
                          textOverflow: "ellipsis" }}
                          title={l.descripcion || ""}>
                          {l.descripcion || "—"}
                        </span>
                      )}
                    </td>
                    {/* DEBE ₡ */}
                    <td style={{ padding: "6px 8px" }}>
                      {editMode ? (
                        <MoneyInput value={l.debito} style={{ ...inpSt, textAlign: "right", color: "#4ade80" }}
                          onChange={v => setMonto(i, "debito", v)}/>
                      ) : (
                        <div style={{ padding: "9px 12px", fontFamily: "monospace", textAlign: "right",
                          color: l.debito > 0 ? "#4ade80" : "var(--border-card)" }}>
                          {l.debito > 0 ? `₡${fmt(l.debito)}` : "—"}
                        </div>
                      )}
                    </td>
                    {/* HABER ₡ */}
                    <td style={{ padding: "6px 8px" }}>
                      {editMode ? (
                        <MoneyInput value={l.credito} style={{ ...inpSt, textAlign: "right", color: "#60a5fa" }}
                          onChange={v => setMonto(i, "credito", v)}/>
                      ) : (
                        <div style={{ padding: "9px 12px", fontFamily: "monospace", textAlign: "right",
                          color: l.credito > 0 ? "#60a5fa" : "var(--border-card)" }}>
                          {l.credito > 0 ? `₡${fmt(l.credito)}` : "—"}
                        </div>
                      )}
                    </td>
                    {/* DEBE $ */}
                    <td style={{ padding: "6px 8px", borderLeft: "1px dashed var(--border-card)" }}>
                      {editMode && multimoneda ? (
                        <MoneyInput value={l.debito_usd} style={{ ...inpSt, textAlign: "right", color: "#86efac" }}
                          onChange={v => setMonto(i, "debito_usd", v)}/>
                      ) : (
                        <div style={{ padding: "9px 12px", fontFamily: "monospace", textAlign: "right",
                          color: Number(l.debito_usd) > 0 ? "#86efac" : "var(--border-card)" }}>
                          {Number(l.debito_usd) > 0 ? `$${fmt(l.debito_usd)}` : "—"}
                        </div>
                      )}
                    </td>
                    {/* HABER $ */}
                    <td style={{ padding: "6px 8px" }}>
                      {editMode && multimoneda ? (
                        <MoneyInput value={l.credito_usd} style={{ ...inpSt, textAlign: "right", color: "#93c5fd" }}
                          onChange={v => setMonto(i, "credito_usd", v)}/>
                      ) : (
                        <div style={{ padding: "9px 12px", fontFamily: "monospace", textAlign: "right",
                          color: Number(l.credito_usd) > 0 ? "#93c5fd" : "var(--border-card)" }}>
                          {Number(l.credito_usd) > 0 ? `$${fmt(l.credito_usd)}` : "—"}
                        </div>
                      )}
                    </td>
                    {editMode && (
                      <td style={{ padding: "4px", textAlign: "center", whiteSpace: "nowrap" }}>
                        {multimoneda && TCnum > 1 && (
                          <button type="button" onClick={() => convertLine(i)}
                            title={`Convertir esta línea al TC ${TCnum} (completa la moneda que falta)`}
                            style={{ background: "none", border: "none", color: "#f97316", cursor: "pointer", fontSize: 13, padding: "2px 5px" }}>
                            <i className="fa-solid fa-right-left"/>
                          </button>
                        )}
                        <button type="button" onClick={() => delL(i)}
                          style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: 14, padding: "2px 6px" }}>
                          <i className="fa-solid fa-times"/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {editMode && (
                  <tr><td colSpan={7} style={{ padding: "6px 8px" }}>
                    <button type="button" onClick={addL}
                      style={{ background: "transparent", border: "1px dashed var(--border-card)",
                        borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer",
                        padding: "5px 12px", fontSize: 12, width: "100%",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      <i className="fa-solid fa-plus"/> {t("asientos.agregarLinea")}
                    </button>
                  </td></tr>
                )}
                <tr style={{ borderTop: "2px solid var(--border-card)", background: "rgba(255,255,255,0.04)" }}>
                  <td colSpan={2} style={{ padding: "10px 12px", color: "var(--text-muted)",
                    fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {t("asientos.totales")}
                  </td>
                  <td><div style={{ padding: "10px 12px", textAlign: "right", color: "#4ade80", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                    ₡{fmt(editMode ? totalD : data.total_debitos)}
                  </div></td>
                  <td><div style={{ padding: "10px 12px", textAlign: "right", color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                    ₡{fmt(editMode ? totalH : data.total_creditos)}
                  </div></td>
                  {/* Totales USD */}
                  <td style={{ borderLeft: "1px dashed var(--border-card)" }}>
                    <div style={{ padding: "10px 12px", textAlign: "right", color: "#86efac", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                      {editMode
                        ? (totalDusd > 0 ? `$${fmt(totalDusd)}` : "—")
                        : (() => { const t$ = (data?.lineas || []).reduce((s, l) => s + (Number(l.debito_usd) || 0), 0); return t$ > 0 ? `$${fmt(t$)}` : "—"; })()}
                    </div>
                  </td>
                  <td>
                    <div style={{ padding: "10px 12px", textAlign: "right", color: "#93c5fd", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                      {editMode
                        ? (totalHusd > 0 ? `$${fmt(totalHusd)}` : "—")
                        : (() => { const t$ = (data?.lineas || []).reduce((s, l) => s + (Number(l.credito_usd) || 0), 0); return t$ > 0 ? `$${fmt(t$)}` : "—"; })()}
                    </div>
                  </td>
                  {editMode && <td/>}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Footer */}
          <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-card)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0, fontSize: 13,
            background: (editMode ? fullyOk : data.balanceado) ? "rgba(74,222,128,0.05)" : "rgba(239,68,68,0.05)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {/* Balance CRC */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <i className={`fa-solid ${(editMode ? balanced : data.balanceado) ? "fa-circle-check" : "fa-triangle-exclamation"}`}
                   style={{ color: (editMode ? balanced : data.balanceado) ? "#4ade80" : "#f87171" }}/>
                <span style={{ color: (editMode ? balanced : data.balanceado) ? "#4ade80" : "#f87171" }}>
                  {(editMode ? balanced : data.balanceado) ? `₡ ${t("asientos.balanceado")}` : `₡ ${t("asientos.noBalanceado")}`}
                </span>
                {editMode && !balanced && (
                  <span style={{ color: "#f87171", fontSize: 12 }}>
                    (Dif: ₡{fmt(Math.abs(totalD - totalH))})
                  </span>
                )}
              </div>
              {/* Balance USD — solo en edit mode cuando multimoneda y hay montos USD */}
              {editMode && multimoneda && hasUsd && (
                <div style={{ display: "flex", alignItems: "center", gap: 6,
                  paddingLeft: 10, borderLeft: "1px dashed var(--border-card)" }}>
                  <i className={`fa-solid ${balancedUsd ? "fa-circle-check" : "fa-triangle-exclamation"}`}
                     style={{ color: balancedUsd ? "#86efac" : "#f87171" }}/>
                  <span style={{ color: balancedUsd ? "#86efac" : "#f87171" }}>
                    {balancedUsd ? `$ ${t("asientos.balanceado")}` : `$ ${t("asientos.noBalanceado")}`}
                  </span>
                  {!balancedUsd && (
                    <span style={{ color: "#f87171", fontSize: 12 }}>
                      (Dif: ${fmt(Math.abs(totalDusd - totalHusd))})
                    </span>
                  )}
                </div>
              )}
            </div>
            {editMode && (
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={() => { setEditMode(false); loadAsiento(); }}
                  style={{ padding: "7px 14px", background: "transparent", border: "1px solid var(--border-card)",
                    borderRadius: 6, color: "var(--text-secondary)", cursor: "pointer", fontSize: 13 }}>
                  {t("common.cancelar")}
                </button>
                <button type="button" onClick={handleSave} disabled={saving || !fullyOk}
                  style={{ padding: "7px 16px",
                    background: fullyOk ? "#f97316" : "var(--border-card)",
                    border: "none", borderRadius: 6,
                    color: fullyOk ? "#fff" : "var(--text-muted)",
                    cursor: fullyOk ? "pointer" : "not-allowed",
                    fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  {saving
                    ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
                    : <><i className="fa-solid fa-floppy-disk"/> {t("cxp.guardarCambios")}</>}
                </button>
              </div>
            )}
          </div>
        </>) : (
          <div style={{ padding: 40, textAlign: "center", color: "#f87171" }}>
            {t("asientos.errorCargar")}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ListAsientos() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const multimoneda = useMultimoneda();
  const [isUSD,       setIsUSD]     = useState(false);
  const [page,        setPage]      = useState(1);
  const [search,      setSearch]    = useState("");
  const [tipo,        setTipo]      = useState("");
  const [fechaDesde,  setFechaDesde] = useState("");
  const [fechaHasta,  setFechaHasta] = useState("");
  const [montoMin,    setMontoMin]  = useState("");
  const [montoMax,    setMontoMax]  = useState("");
  const [detailId,    setDetailId]  = useState(null);
  const [actionMsg,   setActionMsg] = useState(null);
  const [importing,   setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { creados, errores, omitidos, detalle }
  const fileInputRef = useRef(null);

  const params = {
    page,
    ...(search && { search }),
    ...(tipo && { tipo }),
    ...(fechaDesde && { fecha_desde: fechaDesde }),
    ...(fechaHasta && { fecha_hasta: fechaHasta }),
    ...(montoMin && { monto_min: montoMin }),
    ...(montoMax && { monto_max: montoMax }),
  };
  const activeFilterCount = [fechaDesde, fechaHasta, montoMin, montoMax].filter(Boolean).length;
  const { asientos, count, loading, error, refetch } = useAsientos(params);
  const totalPages = Math.max(1, Math.ceil(count / 20));

  // TIPO_LABEL built dynamically so it re-renders on language change
  const TIPO_LABEL = {
    MANUAL:  t("asientos.manual"),
    FACTURA: t("sidebar.facturas"),
    CXP:     "CxP",
    RECIBO:  t("sidebar.recibos"),
  };

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  async function handleDescargarPlantilla() {
    try {
      await downloadExcel("/api/finance/asientos/plantilla/", "plantilla_asientos.xlsx");
    } catch {
      showMsg(false, "Error al descargar la plantilla");
    }
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const data = await uploadExcel("/api/finance/asientos/importar/", file);
      setImportResult(data);
      if ((data.creados ?? 0) > 0) refetch();
    } catch (err) {
      setImportResult({ creados: 0, errores: [err.message || "Error de conexión"], omitidos: 0 });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <>
      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={styles.active}> {t("asientos.titulo")}</span>
      </span>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, margin: "10px 0 20px",
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "var(--text-primary)",
            display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ background: "rgba(249,115,22,0.2)", color: "#f97316", padding: "6px 8px", borderRadius: 8 }}>
              <i className="fa-solid fa-book-open" style={{ color: "#f97316" }}/>
            </span>
            {t("asientos.titulo")}
          </h2>
          <p style={{ margin: "6px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
            {count} {t("asientos.asientosRegistrados")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* ── Plantilla Excel ── */}
          <button
            onClick={handleDescargarPlantilla}
            title="Descargar plantilla Excel para importar asientos"
            style={{
              padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(74,222,128,0.35)",
              background: "rgba(74,222,128,0.08)", color: "#4ade80",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = "rgba(74,222,128,0.18)"; }}
            onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(74,222,128,0.08)"; }}
          >
            <i className="fa-solid fa-file-arrow-down" /> Plantilla
          </button>

          {/* ── Importar Excel ── */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            title="Importar asientos desde Excel"
            style={{
              padding: "8px 14px", borderRadius: 6, border: "1px solid rgba(96,165,250,0.35)",
              background: "rgba(96,165,250,0.08)", color: "#60a5fa",
              fontWeight: 600, fontSize: 13, cursor: importing ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6, transition: "all 0.15s",
              opacity: importing ? 0.6 : 1,
            }}
            onMouseOver={(e) => { if (!importing) e.currentTarget.style.background = "rgba(96,165,250,0.18)"; }}
            onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; }}
          >
            {importing
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Importando…</>
              : <><i className="fa-solid fa-file-arrow-up" /> Importar Excel</>}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />

          {/* ── Nuevo asiento ── */}
          <Link to="/finanzas/asientos/nuevo" className="btn"
            style={{
              padding: "9px 18px", background: "#f97316", borderRadius: 6,
              color: "#fff", textDecoration: "none", fontWeight: 600,
              fontSize: 14, display: "flex", alignItems: "center", gap: 7, border: "none",
            }}>
            <i className="fa-solid fa-plus" /> {t("asientos.nuevoAsientoManual")}
          </Link>
        </div>
      </div>

      {/* Banner acción */}
      {actionMsg && (
        <div style={{
          padding: "10px 16px", marginBottom: 14, borderRadius: 6,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: 14, fontWeight: 500 }}>
            <i className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`}
               style={{ marginRight: 8 }} />
            {actionMsg.text}
          </span>
          <button onClick={() => setActionMsg(null)}
            style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      )}

      {/* Tabla contenedor */}
      <div className={styles["table-container"]}>

        {/* Barra de filtros unificada */}
        <TableToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder={t("asientos.buscarPlaceholder")}
          loading={loading}
          activeFilterCount={activeFilterCount}
          onClearFilters={() => { setFechaDesde(""); setFechaHasta(""); setMontoMin(""); setMontoMax(""); setPage(1); }}
          extra={
            <>
              <ToolbarSelect value={tipo} active={!!tipo}
                onChange={(e) => { setTipo(e.target.value); setPage(1); }}>
                <option value="">{t("asientos.todosTipos")}</option>
                <option value="MANUAL">{t("asientos.manual")}</option>
                <option value="FACTURA">{t("sidebar.facturas")}</option>
                <option value="CXP">CxP</option>
                <option value="RECIBO">{t("sidebar.recibos")}</option>
              </ToolbarSelect>
              {multimoneda && (
                <FilterPills
                  value={isUSD ? "usd" : "crc"}
                  onChange={(v) => setIsUSD(v === "usd")}
                  options={[{ value: "crc", label: "₡ CRC" }, { value: "usd", label: "$ USD" }]}
                />
              )}
            </>
          }
          filters={
            <>
              <FilterField label={t("common.fechaDesde")}>
                <FilterInput type="date" value={fechaDesde}
                  onChange={e => { setFechaDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.fechaHasta")}>
                <FilterInput type="date" value={fechaHasta}
                  onChange={e => { setFechaHasta(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.montoMinimo")}>
                <FilterInput type="number" min="0" step="1000" placeholder="0" value={montoMin}
                  onChange={e => { setMontoMin(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.montoMaximo")}>
                <FilterInput type="number" min="0" step="1000" placeholder={t("common.sinLimite")} value={montoMax}
                  onChange={e => { setMontoMax(e.target.value); setPage(1); }} />
              </FilterField>
            </>
          }
        />

        {/* Tabla */}
        <div className={styles["table-responsive"]}>
          <div className={styles["table-body"]}>
            <table>
              <thead>
                <tr>
                  {[t("asientos.numAsiento"), t("common.fecha"), t("asientos.concepto"),
                    t("asientos.tipo"), t("asientos.debitos"), t("asientos.creditos"),
                    t("asientos.estado"), ""].map((h, i) => (
                    <th key={i} style={{
                      textAlign: [t("asientos.debitos"), t("asientos.creditos")].includes(h) ? "right" : undefined,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 40 }}>
                    <i className="fa-solid fa-circle-notch fa-spin"
                       style={{ color: "#f97316", fontSize: "1.2rem", display: "block", marginBottom: 8 }} />
                    {t("asientos.cargando")}
                  </td></tr>
                ) : error ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#f87171" }}>
                    {error}
                  </td></tr>
                ) : asientos.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
                    {t("asientos.sinAsientos")}
                  </td></tr>
                ) : asientos.map((a) => {
                  const color = TIPO_COLOR[a.tipo] || TIPO_COLOR.MANUAL;
                  return (
                    <tr key={a.id}>
                      <td style={{ fontFamily: "monospace", color: "#f97316", fontWeight: 600, fontSize: 13 }}>
                        {a.numero}
                      </td>
                      <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>{a.fecha}</td>
                      <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
                        {a.concepto}
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px",
                          borderRadius: 20, background: color.bg,
                          border: `1px solid ${color.border}`, color: color.text }}>
                          {TIPO_LABEL[a.tipo] || a.tipo_display || a.tipo}
                        </span>
                      </td>
                      <td>
                        <MontoCelda crc={a.total_debitos} usd={a.total_debitos_usd} isUSD={isUSD} color="#4ade80" />
                      </td>
                      <td>
                        <MontoCelda crc={a.total_creditos} usd={a.total_creditos_usd} isUSD={isUSD} color="#60a5fa" />
                      </td>
                      <td>
                        {a.balanceado
                          ? <i className="fa-solid fa-circle-check" style={{ color: "#4ade80" }} title={t("asientos.balanceado")} />
                          : <i className="fa-solid fa-triangle-exclamation" style={{ color: "#f87171" }} title={t("asientos.noBalanceado")} />
                        }
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button type="button" onClick={() => setDetailId(a.id)}
                            title={t("common.ver")}
                            style={{ background: "rgba(96,165,250,0.1)",
                              border: "1px solid rgba(96,165,250,0.3)", borderRadius: 6,
                              color: "#60a5fa", cursor: "pointer", fontSize: "0.85rem",
                              width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center",
                              justifyContent: "center", transition: "background 0.15s, border-color 0.15s" }}
                            onMouseOver={(e) => { e.currentTarget.style.background = "rgba(96,165,250,0.2)"; e.currentTarget.style.borderColor = "#60a5fa"; }}
                            onMouseOut={(e)  => { e.currentTarget.style.background = "rgba(96,165,250,0.1)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.3)"; }}
                          >
                            <i className="fa-solid fa-eye" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "10px 15px", borderTop: "1px solid var(--border-card)",
          background: "var(--hover-bg)",
        }}>
          <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", margin: 0 }}>
            <span style={{ padding: "0 4px", fontWeight: 600, color: "var(--text-primary)" }}>{count}</span>
            {t("common.resultados")} · {t("common.pagina")}
            <span style={{ padding: "0 4px", fontWeight: 600, color: "var(--text-primary)" }}>{page}</span>
            {t("common.de")}
            <span style={{ padding: "0 4px", fontWeight: 600, color: "var(--text-primary)" }}>{Math.max(1, totalPages)}</span>
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              style={{
                padding: "8px 12px", fontSize: "0.85rem", borderRadius: 5,
                background: "var(--hover-bg)", border: "1px solid var(--border-subtle)",
                color: "var(--text-secondary)", cursor: page <= 1 ? "not-allowed" : "pointer",
                opacity: page <= 1 ? 0.4 : 1, transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => { if (page > 1) { e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.borderColor = "var(--border-input)"; } }}
              onMouseOut={(e)  => { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.borderColor = "var(--border-subtle)"; }}
            >{t("common.anterior")}</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              style={{
                padding: "8px 12px", fontSize: "0.85rem", borderRadius: 5,
                background: "var(--hover-bg)", border: "1px solid var(--border-subtle)",
                color: "var(--text-secondary)", cursor: page >= totalPages ? "not-allowed" : "pointer",
                opacity: page >= totalPages ? 0.4 : 1, transition: "all 0.3s ease",
              }}
              onMouseOver={(e) => { if (page < totalPages) { e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.borderColor = "var(--border-input)"; } }}
              onMouseOut={(e)  => { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.borderColor = "var(--border-subtle)"; }}
            >{t("common.siguiente")}</button>
          </div>
        </div>
      </div>

      {/* Modal detalle / editar / eliminar */}
      {detailId && (
        <AsientoDetalleModal
          asientoId={detailId}
          onClose={() => setDetailId(null)}
          onDeleted={(msg) => { refetch(); showToast(msg || "Asiento eliminado correctamente."); }}
          onSaved={() => { refetch(); showMsg(true, "Concepto actualizado correctamente."); }}
        />
      )}

      {/* ── Modal resultado importación ── */}
      {importResult && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1100, padding: 20,
        }}>
          <div style={{
            background: "var(--bg-card)", borderRadius: 12,
            border: "1px solid var(--border-card)",
            width: "100%", maxWidth: 560,
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
            display: "flex", flexDirection: "column", maxHeight: "85vh",
          }}>
            {/* Header */}
            <div style={{
              padding: "16px 20px", borderBottom: "1px solid var(--border-card)",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: "var(--text-primary)",
                display: "flex", alignItems: "center", gap: 8 }}>
                <i className="fa-solid fa-file-arrow-up" style={{ color: "#60a5fa" }} />
                Resultado de importación
              </span>
              <button onClick={() => setImportResult(null)}
                style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 20px", overflowY: "auto" }}>
              {/* Estadísticas */}
              <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 120, padding: "12px 16px", borderRadius: 8,
                  background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.25)", textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#4ade80" }}>{importResult.creados}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Asientos creados</div>
                </div>
                {(importResult.omitidos ?? 0) > 0 && (
                  <div style={{ flex: 1, minWidth: 120, padding: "12px 16px", borderRadius: 8,
                    background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)", textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#fbbf24" }}>{importResult.omitidos}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Omitidos</div>
                  </div>
                )}
                {(importResult.errores?.length ?? 0) > 0 && (
                  <div style={{ flex: 1, minWidth: 120, padding: "12px 16px", borderRadius: 8,
                    background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#f87171" }}>{importResult.errores.length}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Errores</div>
                  </div>
                )}
              </div>

              {/* Asientos creados */}
              {importResult.detalle?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "#4ade80", marginBottom: 6 }}>
                    <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
                    Asientos importados
                  </p>
                  <div style={{ background: "var(--hover-bg)", borderRadius: 8, overflow: "hidden",
                    border: "1px solid var(--border-card)" }}>
                    {importResult.detalle.map((d, i) => (
                      <div key={i} style={{
                        padding: "7px 12px", fontSize: 13, display: "flex", gap: 10, alignItems: "center",
                        borderBottom: i < importResult.detalle.length - 1 ? "1px solid var(--border-card)" : "none",
                      }}>
                        <span style={{ color: "#a78bfa", fontFamily: "monospace", fontWeight: 700, minWidth: 80 }}>{d.numero}</span>
                        <span style={{ color: "var(--text-muted)", minWidth: 90 }}>{d.fecha}</span>
                        <span style={{ color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.concepto}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errores */}
              {importResult.errores?.length > 0 && (
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "#f87171", marginBottom: 6 }}>
                    <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                    Errores detectados
                  </p>
                  <div style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.2)",
                    borderRadius: 8, padding: "10px 14px", maxHeight: 200, overflowY: "auto" }}>
                    {importResult.errores.map((e, i) => (
                      <p key={i} style={{ margin: "3px 0", fontSize: 12, color: "#f87171" }}>
                        <i className="fa-solid fa-circle-xmark" style={{ marginRight: 6 }} />{e}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-card)",
              display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setImportResult(null)}
                style={{ padding: "8px 20px", background: "#f97316", border: "none",
                  borderRadius: 6, color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {toastEl}
    </>
  );
}
